import React, { useState } from "react";
import { useAtom } from "jotai";
import { useAuth } from "../../context/AuthContext.tsx";
import * as S from "./recurringIncome.style.ts";
import { recurringIncomeAtom } from "../../hook/RecurringIncomeAtom.ts";
import { useMoney } from "../../hook/useMoney.ts";
import { RecurringFrequency, RecurringIncome as Rule } from "../../types.ts";
import { monthlyEquivalent } from "../../functions/cashflow.ts";
import { persistRecurringIncome } from "../../functions/cashflowSync.ts";
import { trackEvent } from "../../functions/mixpanel.ts";
import Button from "../Button/Button.tsx";
import ModalComponent from "../Modal/Modal.tsx";

const FREQUENCIES: RecurringFrequency[] = [
  "Weekly",
  "Every 2 weeks",
  "Semi-Monthly",
  "Monthly",
];

const emptyForm = {
  id: "",
  label: "",
  amount: "",
  frequency: "Every 2 weeks" as RecurringFrequency,
  anchorDate: new Date().toISOString().slice(0, 10),
};

type FormState = typeof emptyForm;

const uuid = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `rec-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const RecurringIncome = () => {
  const auth = useAuth();
  const [rules, setRules] = useAtom(recurringIncomeAtom);
  const { money } = useMoney();

  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState("");

  const monthlyTotal = rules
    .filter((r) => r.active)
    .reduce((sum, r) => sum + monthlyEquivalent(r.amount, r.frequency), 0);

  const openAdd = () => {
    setForm(emptyForm);
    setError("");
    setIsOpen(true);
  };

  const openEdit = (rule: Rule) => {
    setForm({
      id: rule.id,
      label: rule.label || "",
      amount: String(rule.amount),
      frequency: rule.frequency,
      anchorDate: rule.anchorDate,
    });
    setError("");
    setIsOpen(true);
  };

  const save = () => {
    const amount = Number(form.amount);
    if (!form.anchorDate || !amount || amount <= 0) {
      setError("Enter an amount and the date of a recent payday.");
      return;
    }
    if (form.anchorDate > new Date().toISOString().slice(0, 10)) {
      setError("The payday should be a date that has already happened.");
      return;
    }

    const rule: Rule = {
      id: form.id || uuid(),
      label: form.label.trim() || undefined,
      amount,
      frequency: form.frequency,
      anchorDate: form.anchorDate,
      active: true,
    };

    const op = form.id ? "update" : "create";
    setRules((prev) =>
      op === "update"
        ? prev.map((r) => (r.id === rule.id ? rule : r))
        : [...prev, rule],
    );
    persistRecurringIncome(auth, op, rule);
    trackEvent(`Recurring income - ${op}`, { frequency: rule.frequency });
    setIsOpen(false);
  };

  const toggle = (rule: Rule) => {
    const next = { ...rule, active: !rule.active };
    setRules((prev) => prev.map((r) => (r.id === rule.id ? next : r)));
    persistRecurringIncome(auth, "update", next);
  };

  const remove = (rule: Rule) => {
    setRules((prev) => prev.filter((r) => r.id !== rule.id));
    persistRecurringIncome(auth, "delete", rule);
    trackEvent("Recurring income - delete");
  };

  return (
    <S.Wrapper>
      <S.Header>
        <div>
          <h3>Recurring income</h3>
          {rules.length > 0 && (
            <div className="total">
              About {money(monthlyTotal)}/mo from recurring pay
            </div>
          )}
        </div>
        <Button buttonSize="small" handleClick={openAdd}>
          Add paycheck
        </Button>
      </S.Header>

      {rules.length === 0 ? (
        <S.Empty>
          Set your paycheck once — the app fills in each payday for you.
        </S.Empty>
      ) : (
        <S.List>
          {rules.map((rule) => (
            <S.Row key={rule.id}>
              <span className={rule.active ? "" : "muted"}>
                {rule.label || "Paycheck"} · {rule.frequency}
              </span>
              <span className="amount">{money(rule.amount)}</span>
              <span className="muted">
                {money(monthlyEquivalent(rule.amount, rule.frequency))}/mo
              </span>
              <span className="actions">
                <Button
                  buttonSize="small"
                  classType="text"
                  handleClick={() => toggle(rule)}
                >
                  {rule.active ? "Pause" : "Resume"}
                </Button>
                <Button
                  buttonSize="small"
                  classType="text"
                  handleClick={() => openEdit(rule)}
                >
                  Edit
                </Button>
                <Button
                  buttonSize="small"
                  classType="text"
                  handleClick={() => remove(rule)}
                >
                  Delete
                </Button>
              </span>
            </S.Row>
          ))}
        </S.List>
      )}

      <ModalComponent
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={form.id ? "Edit recurring income" : "Add recurring income"}
        size="medium"
      >
        <S.Form>
          <label>
            Description
            <input
              type="text"
              value={form.label}
              placeholder="Paycheck, retainer..."
              onChange={(e) =>
                setForm((f) => ({ ...f, label: e.target.value }))
              }
            />
          </label>
          <div className="row">
            <label>
              Amount per payment (USD)
              <input
                type="number"
                value={form.amount}
                placeholder="0"
                onChange={(e) =>
                  setForm((f) => ({ ...f, amount: e.target.value }))
                }
              />
            </label>
            <label>
              How often
              <select
                value={form.frequency}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    frequency: e.target.value as RecurringFrequency,
                  }))
                }
              >
                {FREQUENCIES.map((freq) => (
                  <option key={freq} value={freq}>
                    {freq}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label>
            Date of a recent payday
            <input
              type="date"
              value={form.anchorDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) =>
                setForm((f) => ({ ...f, anchorDate: e.target.value }))
              }
            />
          </label>
          {error && <div className="error">{error}</div>}
          <div className="buttons">
            <Button
              buttonSize="small"
              classType="exit"
              handleClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button buttonSize="small" handleClick={save}>
              Save
            </Button>
          </div>
        </S.Form>
      </ModalComponent>
    </S.Wrapper>
  );
};

export default RecurringIncome;
